// AdUnit.jsx
import React, { useRef, useEffect, useState, useCallback, memo } from "react";
import axios from "axios";
import usePrebidBidding from "./usePrebidBidding";
import bidService from './bidService';


const PUBLISH_ENDPOINT = "/api/publishCreative";
// NOTE: message bridge removed (you said you track impressions from auctions)

const observerOptions = { root: null, threshold: 0.01, rootMargin: "100px" };
const observer = new IntersectionObserver((entries) => {
  entries.forEach((entry) => entry.target._visibilityCallback?.(entry));
}, observerOptions);

/* simplified: only fire an Image GET for the SSP nurl (no fetch, no sendBeacon) */
function fireNurlOnce(el, url) {
  if (!url || !el) return;
  try {
    el._firedNurl = el._firedNurl || new Set();
    if (el._firedNurl.has(url)) return;
    el._firedNurl.add(url);

    const cb = "ts=" + Date.now();
    const imgUrl = url + (url.includes("?") ? "&" : "?") + cb;

    try {
      const img = new Image();
      img.onload = () => {
        img.onload = img.onerror = null; /* success */
      };
      img.onerror = () => {
        img.onload = img.onerror = null; /* fail - no fallback */
      };
      img.src = imgUrl;
    } catch (e) {
      // swallow
    }
  } catch (e) {
    console.warn("[AdUnit] fireNurlOnce failed", e);
  }
}

/* small helper: extract potential media file URL from VAST XML */
function extractMediaFromVast(vastXml) {
  try {
    if (!vastXml || typeof vastXml !== "string") return null;
    const parser = new DOMParser();
    const xml = parser.parseFromString(vastXml, "text/xml");
    const media = xml.querySelector("MediaFile");
    if (media && media.textContent) return media.textContent.trim();
    return null;
  } catch (e) {
    return null;
  }
}

/* ---------- AdUnit component (with video handling) ---------- */
const AdUnit = memo(({ adUnitCode, onAdRendered }) => {
  if (!adUnitCode) {
    console.warn("[AdUnit] No adUnitCode provided.");
    return null;
  }

  const adRef = useRef(null);
  const adIdRef = useRef(null);
  const viewStart = useRef(null);
  const hasImpressionSent = useRef(false);
  const [isVisible, setIsVisible] = useState(false);
  const iframeCleanupRef = useRef(null);
  const clickHandlerRef = useRef(null);


  // IMPORTANT: usePrebidBidding must return { winningAd, ad_id, nurl, isVideo, width, height }
  const { winningAd, ad_id, nurl: hookNurl, isVideo, width: hookWidth, height: hookHeight } =
  usePrebidBidding(isVisible ? adUnitCode : null);


  useEffect(() => {
    adIdRef.current = ad_id;
  }, [ad_id]);

  const queueUpdate = useCallback((click = 0, impression = 0, additionalViewTime = 0) => {
    const viewability = additionalViewTime ? Math.min(additionalViewTime / 1000, 10).toFixed(2) : null;
    if (!adIdRef.current) return;
    try {
      window.__AD_UPDATE_QUEUE__ = window.__AD_UPDATE_QUEUE__ || [];
      window.__AD_UPDATE_QUEUE__.push({ ad_id: adIdRef.current, click, impression, viewability_score: viewability });
    } catch (e) {}
  }, []);

  const handleVisibilityChange = useCallback(
    (entry) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        viewStart.current = performance.now();
      } else {
        setIsVisible(false);
        if (viewStart.current) {
          const elapsed = performance.now() - viewStart.current;
          queueUpdate(0, 0, elapsed);
          viewStart.current = null;
        }
      }
    },
    [queueUpdate]
  );

  useEffect(() => {
    const el = adRef.current;
    if (!el) return;
    el._visibilityCallback = handleVisibilityChange;
    observer.observe(el);
    return () => {
      try {
        observer.unobserve(el);
      } catch (e) {}
      delete el._visibilityCallback;
    };
  }, [handleVisibilityChange]);

  const cleanupIframeMount = useCallback(() => {
    // run any specialized cleanup (remove iframe/anchor, revoke blob url, remove hint listeners)
    if (iframeCleanupRef.current) {
      try {
        iframeCleanupRef.current();
      } catch (e) {}
      iframeCleanupRef.current = null;
    }

    try {
      // pause any playing video inside container
      const vid = adRef.current && adRef.current.querySelector && adRef.current.querySelector("video");
      if (vid && typeof vid.pause === "function") {
        try {
          vid.pause();
        } catch (e) {}
      }

      if (adRef.current && clickHandlerRef.current) {
        adRef.current.removeEventListener("click", clickHandlerRef.current);
        clickHandlerRef.current = null;
      }
    } catch (e) {}
  }, []);

  const addContainerClickListenerOnce = useCallback(() => {
    if (!adRef.current || clickHandlerRef.current) return;
    const handler = () => queueUpdate(1);
    clickHandlerRef.current = handler;
    adRef.current.addEventListener("click", handler, { once: true });
  }, [queueUpdate]);

  const mountImage = useCallback(
    (src) => {
      if (!adRef.current) return false;
      cleanupIframeMount();
      adRef.current.innerHTML = "";
      const img = document.createElement("img");
      img.src = src.startsWith("//") ? `https:${src}` : src;
      img.alt = "Ad";
      img.style.display = "block";
      img.onclick = () => queueUpdate(1);
      img.oncontextmenu = (e) => {
        queueUpdate(1);
      };
      adRef.current.appendChild(img);
      addContainerClickListenerOnce();
      return true;
    },
    [cleanupIframeMount, queueUpdate, addContainerClickListenerOnce]
  );

  const mountIframeSrc = useCallback(
    (src, parentAnchorHref = null) => {
      if (!adRef.current) return false;
      cleanupIframeMount();
      adRef.current.innerHTML = "";

      const trustedSspHosts = ["cdn.bmcdn7.com", "media.bmcdn7.com", "bmcdn7.com", "https://onetag-sys.com"];
      let urlHostname = "";
      try {
        urlHostname = new URL(src).hostname;
      } catch (e) {
        urlHostname = "";
      }

      const iframe = document.createElement("iframe");
      /* if (!trustedSspHosts.some((h) => urlHostname.endsWith(h))) {
        iframe.setAttribute("allow-same-origin", "allow-scripts allow-popups allow-top-navigation-by-user-activation");
      } */

      try {
        iframe.style.cursor = "pointer";
      } catch (e) {}
      iframe.src = src;
      iframe.style.display = "block";
      iframe.style.margin = "0 auto";
      iframe.style.border = "0";
      iframe.style.boxSizing = "border-box";
      
      // buffer for safety; tweak if you still see tiny clipping
      const BUFFER_PX = 8;

      // width handling: if hookWidth provided, use explicit px width + buffer (keeps aspect)
      if (Number.isFinite(hookWidth) && hookWidth > 0) {
        const w = Math.max(1, Math.round(hookWidth)) + BUFFER_PX;
        iframe.style.width = `${w}px`;
        iframe.setAttribute("width", String(w));
        // restrict maxWidth to avoid overflow on tiny containers
        iframe.style.maxWidth = `${w}px`;
      } else {
        // fallback: allow responsive but provide a safe minWidth so creative has space
        iframe.style.width = "100%";
      }

      // height handling: if hookHeight provided, use explicit px height + buffer
      if (Number.isFinite(hookHeight) && hookHeight > 0) {
        const h = Math.max(1, Math.round(hookHeight)) + BUFFER_PX;
        iframe.style.height = `${h}px`;
        iframe.setAttribute("height", String(h));
        iframe.style.maxHeight = `${h}px`;
      } else {
        // fallback minHeight so creative isn't cropped
        iframe.style.minHeight = "70px";
        // don't set hard height so tall creatives can expand (if same-origin resizing is used)
      }

      if (parentAnchorHref) {
        const a = document.createElement("a");
        a.href = parentAnchorHref;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        a.style.display = "inline-block";
        a.style.width = "100%";

        try {
          iframe.style.pointerEvents = "none";
        } catch (e) {}

        a.appendChild(iframe);
        adRef.current.appendChild(a);

        iframeCleanupRef.current = () => {
          try {
            a.remove();
          } catch (e) {}
        };

        addContainerClickListenerOnce();
        return true;
      }

      adRef.current.appendChild(iframe);
      iframeCleanupRef.current = () => {
        try {
          iframe.remove();
        } catch (e) {}
      };

      addContainerClickListenerOnce();

      iframe.addEventListener("load", () => {
        try {
          // optional server-side log — safe to keep if you want fallback telemetry
          axios.post("/api/fallbackImpression", { ad_id: adIdRef.current, event: "iframeLoaded", url: src, ts: Date.now() }).catch(() => {});
        } catch (e) {}
      });

      return true;
    },
    [cleanupIframeMount, addContainerClickListenerOnce, hookWidth, hookHeight]
  );

  const publishCreativeToWrapper = useCallback(async (admHtml) => {
    if (!admHtml || typeof admHtml !== "string") return null;
    // AbortController + axios: ensure your axios supports the `signal` param (axios >= 1.2+)
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    try {
      const resp = await axios.post(PUBLISH_ENDPOINT, { adm: admHtml }, { signal: controller.signal });
      return resp?.data?.url || null;
    } catch (err) {
      return null;
    } finally {
      clearTimeout(timeout);
    }
  }, []);

  /* mount video: prefer direct media URL from VAST xml, else publish wrapper */
  const mountVideoFromWin = useCallback(
    async (win) => {
      if (!adRef.current) return false;
      cleanupIframeMount();
      adRef.current.innerHTML = "";

      const vastXml = (win && (win.vastXml || (typeof win.adm === "string" && win.adm) || (typeof win.ad === "string" && win.ad))) || null;
      const vastUrlCandidate = (win && (win.vastUrl || win.vast_url || win.vast)) || null;

      if (vastXml && typeof vastXml === "string") {
        const media = extractMediaFromVast(vastXml);
        if (media) {
          const video = document.createElement("video");
          video.controls = true;
          video.width = 640;
          video.height = 360;
          video.style.display = "block";
          video.preload = "metadata";
          video.playsInline = true;
          video.setAttribute("webkit-playsinline", "true");

          const s = document.createElement("source");
          s.src = media;
          if (/\.(mp4)(\?.*)?$/i.test(media)) s.type = "video/mp4";
          video.appendChild(s);
          video.onclick = () => queueUpdate(1);
          adRef.current.appendChild(video);
          addContainerClickListenerOnce();
          return true;
        }
      }

      try {
        if (vastUrlCandidate && typeof vastUrlCandidate === "string" && /\.(mp4|webm|ogg)(\?.*)?$/i.test(vastUrlCandidate)) {
          const video = document.createElement("video");
          video.controls = true;
          video.width = 640;
          video.height = 360;
          video.style.display = "block";
          const s = document.createElement("source");
          s.src = vastUrlCandidate;
          if (/\.(mp4)(\?.*)?$/i.test(vastUrlCandidate)) s.type = "video/mp4";
          video.appendChild(s);
          video.onclick = () => queueUpdate(1);
          adRef.current.appendChild(video);
          addContainerClickListenerOnce();
          return true;
        }
      } catch (e) {}

      const admStr = (typeof win.adm === "string" && win.adm) || (typeof win.ad === "string" && win.ad) || "";
      if (admStr) {
        const wrapper = await publishCreativeToWrapper(admStr);
        if (wrapper) {
          await mountIframeSrc(wrapper);
          return true;
        }
      }

      return false;
    },
    [cleanupIframeMount, publishCreativeToWrapper, mountIframeSrc, addContainerClickListenerOnce, queueUpdate]
  );

// helper: safely render Prebid native JSON into the ad container
async function renderNative(win, container, { queueUpdate, addContainerClickListenerOnce }) {
  if (!win || !container) return false;

  // Prebid native payload might be in win.native or in JSON string win.adm
  const nativePayload = win.native || (typeof win.adm === 'string' ? tryParseJson(win.adm) : null);

  if (!nativePayload) return false;

  // Typical Prebid native mapping: native.title, native.image.url, native.body, native.clickUrl, native.sponsoredBy, native.impressionTrackers
  const title = nativePayload.title || (nativePayload.title && nativePayload.title.text) || null;
  const image = (nativePayload.image && (nativePayload.image.url || nativePayload.image.src)) || null;
  const body = nativePayload.body || (nativePayload.body && nativePayload.body.text) || null;
  const sponsored = nativePayload.sponsoredBy || (nativePayload.sponsored && nativePayload.sponsored.text) || null;
  const cta = nativePayload.cta || (nativePayload.cta && nativePayload.cta.text) || 'Learn more';
  const clickUrl = nativePayload.clickUrl || nativePayload.link?.url || (nativePayload.action && nativePayload.action.url) || null;
  const impressionTrackers = nativePayload.impressionTrackers || nativePayload.imptrackers || nativePayload.impression_urls || [];

  // Build DOM nodes (safer than innerHTML)
  try {
    container.innerHTML = '';
    const wrapper = document.createElement('div');
    wrapper.className = 'native-ad-wrapper';

    if (image) {
      const img = document.createElement('img');
      img.src = image.startsWith('//') ? `https:${image}` : image;
      img.alt = title || 'Ad';
      img.style.maxWidth = '100%';
      wrapper.appendChild(img);
    }

    if (title) {
      const h = document.createElement('h3');
      h.textContent = title;
      wrapper.appendChild(h);
    }

    if (body) {
      const p = document.createElement('p');
      p.textContent = body;
      wrapper.appendChild(p);
    }

    if (sponsored) {
      const s = document.createElement('div');
      s.className = 'native-sponsored';
      s.textContent = sponsored;
      wrapper.appendChild(s);
    }

    if (clickUrl) {
      const a = document.createElement('a');
      a.href = clickUrl;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.className = 'native-cta';
      a.textContent = cta || 'Learn more';
      // ensure clicks count
      a.addEventListener('click', () => queueUpdate(1));
      // wrap content in anchor
      // move wrapper children inside anchor
      const inner = wrapper.cloneNode(true);
      a.innerHTML = inner.innerHTML;
      container.appendChild(a);
    } else {
      container.appendChild(wrapper);
    }

    // fire impression trackers with Image GETs when element becomes visible
    container._impressionTrackers = (container._impressionTrackers || []).concat(impressionTrackers);
    container._trackersFired = false;

    // attach click listener to container (once) for generic click counts
    addContainerClickListenerOnce();

    return true;
  } catch (e) {
    console.warn('[AdUnit] renderNative failed', e);
    return false;
  }
}

function tryParseJson(str) {
  try {
    const parsed = JSON.parse(str);
    // some bidders wrap native in {native: {...}}
    if (parsed && parsed.native) return parsed.native;
    return parsed;
  } catch (e) {
    return null;
  }
}

  const renderCreative = useCallback(
    async (win) => {
      if (!adRef.current) return false;
      cleanupIframeMount();
      adRef.current.innerHTML = "";

      // Try native first
      const nativeOk = await renderNative(win, adRef.current, { queueUpdate, addContainerClickListenerOnce });
      if (nativeOk) return true;

      let adm = "";
      if (typeof win === "string") adm = win;
      else if (win && typeof win === "object") adm = win.adm || win.ad || win.html || win.creative || "";
      else adm = String(win || "");

      try {
        const trackers = extractImpressionUrlsFromHtml(adm || "");
        adRef.current._impressionTrackers = trackers;
        adRef.current._trackersFired = false;
      } catch (e) {}

      const trimmed = (adm || "").trim();

      if (isVideo) {
        const ok = await mountVideoFromWin(win);
        if (ok) return true;
      }

      try {
        const tmp = document.createElement("div");
        tmp.innerHTML = trimmed;

        const nestedIframe = tmp.querySelector && tmp.querySelector('iframe[src^="http"]');
        if (nestedIframe) {
          const parentAnchor = nestedIframe.closest && nestedIframe.closest("a");
          const src = nestedIframe.getAttribute("src") || nestedIframe.src;
          if (src) {
            await mountIframeSrc(src, parentAnchor ? parentAnchor.getAttribute("href") : null);
            return true;
          }
        }

        const imgTag = tmp.querySelector && tmp.querySelector("img");
        if (imgTag && imgTag.getAttribute("src")) {
          mountImage(imgTag.getAttribute("src"));
          return true;
        }

        if (/^https?:\/\/.*\.(jpe?g|png|gif|webp)(\?.*)?$/i.test(trimmed)) {
          mountImage(trimmed);
          return true;
        }
      } catch (e) {}

      try {
        const wrapper = await publishCreativeToWrapper(trimmed);
        if (wrapper) {
          await mountIframeSrc(wrapper);
          return true;
        }
      } catch (e) {}

      return false;
    },
    [cleanupIframeMount, mountIframeSrc, mountImage, publishCreativeToWrapper, isVideo, mountVideoFromWin]
  );

  /* consolidated effect: render and fire nurl */
  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!winningAd || !adRef.current) return;

      const adIdKey =
        winningAd && typeof winningAd === "object"
          ? (winningAd.adId || winningAd.ad_id || winningAd.requestId || winningAd.id)
          : null;
      const keyToSet = adIdKey || ad_id || null;
      if (keyToSet && adRef.current.getAttribute("data-ad-loaded") === String(keyToSet)) {
        return;
      }
      if (keyToSet) adRef.current.setAttribute("data-ad-loaded", String(keyToSet));

      await renderCreative(winningAd);

      if (isVisible && hookNurl) {
        try {
          new URL(hookNurl);
          fireNurlOnce(adRef.current, hookNurl);
          if (!hasImpressionSent.current) {
            hasImpressionSent.current = true;
            queueUpdate(0, 1);
          }
        } catch (e) {
          // invalid hookNurl -> skip
        }
      }

      if (!cancelled) onAdRendered?.(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [winningAd, isVisible, hookNurl, renderCreative, onAdRendered, ad_id, queueUpdate]);

  useEffect(() => {
    if (!isVisible || !adUnitCode) return;

    const ROTATE_MS = 60_000;

    // immediate trigger only if tab is active
    if (!document.hidden) {
      try { bidService.requestBidsForAdUnit(adUnitCode); } catch (e) {}
    }

    const interval = setInterval(() => {
      if (document.hidden) return;
      try { bidService.requestBidsForAdUnit(adUnitCode); } catch (e) {}
    }, ROTATE_MS);

    return () => clearInterval(interval);
  }, [isVisible, adUnitCode]);


  /* cleanup on unmount */
  useEffect(() => {
    return () => {
      try {
        if (adRef.current) adRef.current.innerHTML = "";
      } catch (e) {}
      cleanupIframeMount();
    };
  }, [cleanupIframeMount]);

  const styleClass = "transition-opacity duration-1000 ease-in-out flex justify-center items-center w-full";
  return (
    <div
      id={adUnitCode}
      ref={adRef}
      className={styleClass}
      style={{
        cursor: "pointer",
        marginTop: 0,
        marginBottom: 0,
        paddingTop: 0,
        paddingBottom: 0,
      }}
    />
  );
});

export default AdUnit;
